import React from "react";
import {
  ArrowLeft, ArrowRight, TrendingUp, Activity, Heart, LineChart,
  Award, Users, Sparkles, ExternalLink, BookOpen, FileText,
} from "lucide-react";

const T = {
  cream: "#F5F1EB",
  forest: "#0F0F0F",
  gold: "#EC6B1F",
  goldSoft: "#F5842B",
  ink: "#0F0F0F",
  softInk: "#4a4a4a",
  line: "#E8E2D5",
};

const serif = { fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif" };
const sans = { fontFamily: "'Inter', -apple-system, sans-serif" };

// Fonti / riviste scientifiche di riferimento
const JOURNALS = [
  {
    nome: "Journal of Sport and Health Science",
    abbr: "JSHS",
    editore: "Elsevier · Shanghai University of Sport",
    if: "IF 13.1",
    url: "https://www.journals.elsevier.com/journal-of-sport-and-health-science",
    accent: "#C8102E",
  },
  {
    nome: "PubMed",
    abbr: "PubMed",
    editore: "US National Library of Medicine",
    if: "35M+ articoli",
    url: "https://pubmed.ncbi.nlm.nih.gov/",
    accent: "#20558A",
  },
  {
    nome: "American Heart Association",
    abbr: "AHA",
    editore: "Circulation · Heart",
    if: "IF 39.9",
    url: "https://www.heart.org/",
    accent: "#C10230",
  },
  {
    nome: "The Lancet",
    abbr: "Lancet",
    editore: "Elsevier",
    if: "IF 168.9",
    url: "https://www.thelancet.com/",
    accent: "#000000",
  },
  {
    nome: "JAMA",
    abbr: "JAMA",
    editore: "American Medical Association",
    if: "IF 63.1",
    url: "https://jamanetwork.com/",
    accent: "#B41F1F",
  },
  {
    nome: "British Journal of Sports Medicine",
    abbr: "BJSM",
    editore: "BMJ Publishing",
    if: "IF 18.4",
    url: "https://bjsm.bmj.com/",
    accent: "#F26722",
  },
];

// Pubblicazioni chiave sul VO2max
const PAPERS = [
  {
    titolo: "Association between cardiorespiratory fitness and mortality",
    autori: "Singh N. et al.",
    anno: "2024",
    rivista: "Journal of Sport and Health Science",
    tipo: "Systematic Review + Meta-Analysis",
    sample: "3.813.484 osservazioni · 42 studi · 35 coorti",
    finding: "Ogni +1 MET di fitness cardiorespiratoria è associato a −14% mortalità totale e −16% mortalità cardiovascolare. La CRF stimata (senza laboratorio) mantiene lo stesso valore prognostico della CRF misurata.",
    url: "https://pubmed.ncbi.nlm.nih.gov/",
  },
  {
    titolo: "Importance of Assessing Cardiorespiratory Fitness in Clinical Practice",
    autori: "Ross R. et al. (AHA Scientific Statement)",
    anno: "2016",
    rivista: "Circulation",
    tipo: "AHA Scientific Statement",
    sample: "Position paper ufficiale AHA",
    finding: "La CRF è un vital sign clinico. Dovrebbe essere valutata routinariamente come pressione, colesterolo e BMI. Un basso VO₂max è un predittore di mortalità più forte del fumo o del diabete.",
    url: "https://www.ahajournals.org/doi/10.1161/CIR.0000000000000461",
  },
  {
    titolo: "Cardiorespiratory Fitness and All-Cause Mortality",
    autori: "Mandsager K. et al.",
    anno: "2018",
    rivista: "JAMA Network Open",
    tipo: "Cohort study",
    sample: "122.007 pazienti · Cleveland Clinic",
    finding: "Chi ha CRF elite ha mortalità 5 volte inferiore rispetto ai low-performer. Non esiste una soglia oltre la quale i benefici si annullano: più fitness = più longevità.",
    url: "https://jamanetwork.com/journals/jamanetworkopen/fullarticle/2707428",
  },
];

const LongevityVO2MaxPage = () => {
  return (
    <div style={{ backgroundColor: T.cream, color: T.ink, ...sans }} data-testid="vo2max-page">

      {/* TOP BAR */}
      <div className="w-full px-4 md:px-14 py-3 md:py-4 flex items-center justify-between gap-2 border-b" style={{ borderColor: T.line, backgroundColor: T.cream }}>
        <a href="/longevity" className="inline-flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs uppercase tracking-[2px] md:tracking-[3px]" style={{ color: T.softInk }} data-testid="btn-back">
          <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Longevity Academy</span><span className="sm:hidden">Home</span>
        </a>
        <div className="text-[10px] md:text-xs tracking-[2px] md:tracking-[4px] uppercase font-semibold text-center flex-1 md:flex-none" style={{ color: T.gold }}>VO₂ Max</div>
        <a href="#pubblicazioni" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[2px] px-4 py-2 border" style={{ borderColor: T.gold, color: T.forest }}>
          <BookOpen className="w-4 h-4" /> Pubblicazioni
        </a>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden" style={{ backgroundColor: T.forest }} data-testid="vo2-hero">
        <div className="absolute inset-0 opacity-25 pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, ${T.gold}66, transparent 55%), radial-gradient(circle at 85% 80%, ${T.goldSoft}44, transparent 55%)`
        }} />
        <div className="relative max-w-6xl mx-auto px-4 md:px-14 py-16 md:py-32">
          <div className="inline-flex items-center gap-2 mb-6 md:mb-8 px-3 md:px-4 py-2 rounded-full" style={{ backgroundColor: `${T.gold}22`, border: `1px solid ${T.gold}` }}>
            <Sparkles className="w-4 h-4" style={{ color: T.goldSoft }} />
            <span className="text-[10px] md:text-xs tracking-[3px] uppercase font-bold" style={{ color: T.goldSoft }}>La scienza che allunga la vita</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-light leading-[1.05] mb-6 md:mb-8 text-white" style={serif}>
            Il metodo <em style={{ color: T.goldSoft, fontStyle: "italic" }}>VO₂ MAX</em>.<br />
            <span className="text-2xl sm:text-3xl md:text-5xl">Fitness cardiorespiratoria = <em style={{ color: T.goldSoft, fontStyle: "italic" }}>longevità.</em></span>
          </h1>
          <p className="text-white/80 text-base md:text-xl leading-relaxed max-w-3xl mb-6 font-light">
            Su <strong style={{ color: T.goldSoft }}>3.813.484 persone</strong> analizzate in meta-analisi, il risultato è chiaro:
            più il tuo cuore-polmoni funzionano bene, più basso è il rischio di morire.
            E la buona notizia è che <strong style={{ color: T.goldSoft }}>non serve un laboratorio</strong> —
            noi ti monitoriamo a distanza con wearable e intelligenza artificiale.
          </p>
          <p className="text-white/70 text-sm md:text-base leading-relaxed max-w-3xl mb-8 md:mb-10 font-light italic" style={{ borderLeft: `3px solid ${T.gold}`, paddingLeft: "1rem" }}>
            Il monitoraggio VO2Max è parte integrante della Longevity Academy: ti accompagna per le 8 settimane del corso e per le 4 settimane di follow-up dopo il diploma.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
            <a href="#inizia" className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3.5 md:py-4 text-xs md:text-sm tracking-[2px] uppercase font-bold transition-all hover:scale-[1.02] shadow-2xl"
               style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="cta-inizia">
              Voglio iniziare <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#pubblicazioni" className="inline-flex items-center justify-center gap-3 px-6 md:px-8 py-3.5 md:py-4 text-xs md:text-sm tracking-[2px] uppercase font-bold border transition-colors hover:bg-white/10"
               style={{ borderColor: T.cream, color: T.cream }} data-testid="cta-scienza">
              Vedi la scienza <BookOpen className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* 2 STAT ESPLOSIVE */}
      <section className="py-16 md:py-24 px-4 md:px-14" style={{ backgroundColor: T.forest }} data-testid="stats">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] md:text-xs tracking-[6px] uppercase mb-4 md:mb-6" style={{ color: T.goldSoft }}>I numeri della scienza</p>
          <h2 className="text-white text-2xl sm:text-3xl md:text-5xl font-light mb-10 md:mb-14 max-w-3xl leading-tight" style={serif}>
            Ogni <em style={{ color: T.goldSoft, fontStyle: "italic" }}>+1 MET</em> di fitness cardiorespiratoria vale una vita più lunga.
          </h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-10">
            <div className="p-6 md:p-10 border-2 relative overflow-hidden" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.08)" }} data-testid="stat-mortalita">
              <div className="text-6xl sm:text-7xl md:text-9xl font-bold mb-2 leading-none" style={{ color: T.goldSoft, ...serif }}>−14<span className="text-3xl md:text-5xl">%</span></div>
              <p className="text-white/90 text-base md:text-lg font-semibold mb-2 mt-3">Mortalità per tutte le cause</p>
              <p className="text-white/60 text-xs md:text-sm leading-relaxed">Riduzione del rischio associata a ogni <strong>+1 MET</strong> di fitness cardiorespiratoria (≈ +3,5 ml/kg/min di VO₂).</p>
            </div>
            <div className="p-6 md:p-10 border-2 relative overflow-hidden" style={{ borderColor: T.gold, backgroundColor: "rgba(236,107,31,0.08)" }} data-testid="stat-cardio">
              <div className="text-6xl sm:text-7xl md:text-9xl font-bold mb-2 leading-none" style={{ color: T.goldSoft, ...serif }}>−16<span className="text-3xl md:text-5xl">%</span></div>
              <p className="text-white/90 text-base md:text-lg font-semibold mb-2 mt-3">Mortalità cardiovascolare</p>
              <p className="text-white/60 text-xs md:text-sm leading-relaxed">Riduzione del rischio per ogni <strong>+1 MET</strong>. Anche piccoli aumenti di fitness cambiano radicalmente la prognosi.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/10">
            <div><div className="text-2xl md:text-3xl font-bold" style={{ color: T.goldSoft, ...serif }}>3,8M</div><p className="text-white/60 text-[10px] md:text-xs uppercase tracking-widest mt-1">osservazioni</p></div>
            <div><div className="text-2xl md:text-3xl font-bold" style={{ color: T.goldSoft, ...serif }}>42</div><p className="text-white/60 text-[10px] md:text-xs uppercase tracking-widest mt-1">studi</p></div>
            <div><div className="text-2xl md:text-3xl font-bold" style={{ color: T.goldSoft, ...serif }}>35</div><p className="text-white/60 text-[10px] md:text-xs uppercase tracking-widest mt-1">coorti</p></div>
            <div><div className="text-2xl md:text-3xl font-bold" style={{ color: T.goldSoft, ...serif }}>2024</div><p className="text-white/60 text-[10px] md:text-xs uppercase tracking-widest mt-1">pubblicato</p></div>
          </div>
        </div>
      </section>

      {/* 3 CARD METODO */}
      <section id="metodo" className="py-24 px-6 md:px-14" data-testid="metodo">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[6px] uppercase mb-4" style={{ color: T.gold }}>Come lo facciamo</p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T.forest, ...serif }}>
              Senza laboratorio. <em style={{ fontStyle: "italic", color: T.softInk }}>Senza spostamenti.</em>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-10 bg-white shadow-xl border-t-4 hover:-translate-y-2 transition-transform" style={{ borderColor: T.gold }} data-testid="metodo-1">
              <Activity className="w-12 h-12 mb-6" style={{ color: T.gold }} />
              <h3 className="text-2xl mb-3 font-light" style={{ color: T.forest, ...serif }}>Wearable + AI</h3>
              <p className="text-sm leading-relaxed" style={{ color: T.softInk }}>
                Il tuo smartwatch/fascia cardio invia dati in continuo. Un modello AI stima il tuo VO₂max e il tuo percentile di fitness per età e sesso — con precisione validata scientificamente.
              </p>
            </div>
            <div className="p-10 bg-white shadow-xl border-t-4 hover:-translate-y-2 transition-transform" style={{ borderColor: T.gold }} data-testid="metodo-2">
              <Heart className="w-12 h-12 mb-6" style={{ color: T.gold }} />
              <h3 className="text-2xl mb-3 font-light" style={{ color: T.forest, ...serif }}>Monitoraggio remoto</h3>
              <p className="text-sm leading-relaxed" style={{ color: T.softInk }}>
                HR a riposo, HR recovery, velocità di camminata, sonno. Per le 8 settimane del corso e le 4 di follow-up, ogni settimana il tuo coach Longevity Academy vede i tuoi progressi e adatta il piano — anche se sei a 500 km da noi.
              </p>
            </div>
            <div className="p-10 bg-white shadow-xl border-t-4 hover:-translate-y-2 transition-transform" style={{ borderColor: T.gold }} data-testid="metodo-3">
              <LineChart className="w-12 h-12 mb-6" style={{ color: T.gold }} />
              <h3 className="text-2xl mb-3 font-light" style={{ color: T.forest, ...serif }}>Longevity Score</h3>
              <p className="text-sm leading-relaxed" style={{ color: T.softInk }}>
                Un unico numero che sintetizza fitness cardiorespiratoria, forza, composizione corporea e stile di vita. Aggiornato mensilmente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RIVISTE SCIENTIFICHE — Loghi */}
      <section className="py-20 px-6 md:px-14" style={{ backgroundColor: "#EBE4D6" }} data-testid="riviste">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[6px] uppercase mb-4" style={{ color: T.gold }}>Fonti autorevoli</p>
            <h2 className="text-3xl md:text-4xl font-light" style={{ color: T.forest, ...serif }}>
              Il nostro metodo si basa sulla <em style={{ fontStyle: "italic", color: T.softInk }}>letteratura peer-reviewed</em>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {JOURNALS.map((j, i) => (
              <a key={i} href={j.url} target="_blank" rel="noopener noreferrer"
                 className="group p-6 bg-white border-2 border-transparent hover:border-orange-500 transition-all hover:shadow-2xl hover:-translate-y-1 flex flex-col items-center text-center"
                 data-testid={`rivista-${i}`}>
                <div className="w-full h-20 flex items-center justify-center mb-4 border-b-2" style={{ borderColor: j.accent + "22" }}>
                  <span className="text-3xl md:text-4xl font-black tracking-tight" style={{ color: j.accent, fontFamily: i === 3 ? "'Times New Roman', serif" : (i === 4 ? "'Georgia', serif" : "'Inter', sans-serif"), fontStyle: i === 3 ? "italic" : "normal" }}>
                    {j.abbr}
                  </span>
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: T.forest }}>{j.nome}</p>
                <p className="text-xs mb-2" style={{ color: T.softInk }}>{j.editore}</p>
                <span className="text-xs px-2 py-1 rounded-full font-bold" style={{ backgroundColor: T.gold + "20", color: T.gold }}>{j.if}</span>
                <ExternalLink className="w-3 h-3 mt-3 opacity-40 group-hover:opacity-100" style={{ color: T.gold }} />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* PUBBLICAZIONI CHIAVE */}
      <section id="pubblicazioni" className="py-24 px-6 md:px-14" data-testid="pubblicazioni">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs tracking-[6px] uppercase mb-4" style={{ color: T.gold }}>Studi di riferimento</p>
            <h2 className="text-4xl md:text-5xl font-light" style={{ color: T.forest, ...serif }}>
              3 pubblicazioni <em style={{ fontStyle: "italic", color: T.softInk }}>che cambiano tutto</em>
            </h2>
          </div>
          <div className="space-y-6">
            {PAPERS.map((p, i) => (
              <div key={i} className="p-8 md:p-10 bg-white border-l-4 shadow-lg hover:shadow-2xl transition-shadow" style={{ borderColor: T.gold }} data-testid={`paper-${i}`}>
                <div className="flex flex-wrap gap-3 mb-4">
                  <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ backgroundColor: T.forest, color: T.goldSoft }}>{p.tipo}</span>
                  <span className="text-xs px-3 py-1 rounded-full font-bold" style={{ backgroundColor: T.gold + "20", color: T.gold }}>{p.anno}</span>
                  <span className="text-xs px-3 py-1 rounded-full font-semibold border" style={{ borderColor: T.line, color: T.softInk }}>{p.rivista}</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-light mb-3" style={{ color: T.forest, ...serif }}>{p.titolo}</h3>
                <p className="text-sm font-semibold mb-4" style={{ color: T.softInk }}>{p.autori} · <em>{p.sample}</em></p>
                <p className="text-base leading-relaxed mb-4" style={{ color: T.ink }}>
                  <FileText className="w-4 h-4 inline mr-2" style={{ color: T.gold }} />
                  {p.finding}
                </p>
                <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold hover:underline" style={{ color: T.gold }}>
                  Leggi lo studio <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CALLOUT SEDENTARI */}
      <section id="inizia" className="py-24 px-6 md:px-14" style={{ backgroundColor: T.forest }} data-testid="callout-sedentari">
        <div className="max-w-5xl mx-auto">
          <div className="relative p-10 md:p-14 border-l-4 overflow-hidden" style={{ borderColor: T.gold, backgroundColor: "#000" }}>
            <div className="grid md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <p className="text-xs tracking-[3px] uppercase font-bold mb-3" style={{ color: T.goldSoft }}>Per chi non fa sport da anni</p>
                <h3 className="text-white text-3xl md:text-5xl font-light leading-tight mb-6" style={serif}>
                  Sei fermo da troppo tempo?<br />
                  <em style={{ color: T.goldSoft, fontStyle: "italic" }}>Ripartiamo insieme.</em>
                </h3>
                <p className="text-white/75 text-base md:text-lg leading-relaxed mb-8">
                  Chi ha bassa fitness cardiorespiratoria è la persona che ha <strong style={{ color: T.goldSoft }}>più da guadagnare</strong> —
                  scientificamente. Non serve palestra, non serve viaggio: il percorso Longevity Academy dura <strong style={{ color: T.goldSoft }}>8 settimane</strong>, con lezioni live e monitoraggio wearable incluso, seguite da <strong style={{ color: T.goldSoft }}>4 settimane di follow-up</strong> per consolidare i risultati. Dodici settimane in tutto, monitorate da remoto con app e coach dedicato.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a href="/longevity#contatti"
                     className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-bold transition-all hover:scale-[1.02] shadow-2xl"
                     style={{ backgroundColor: T.gold, color: "#fff" }} data-testid="btn-iscriviti">
                    Voglio iscrivermi <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href="/longevity/aziende"
                     className="inline-flex items-center gap-3 px-8 py-4 text-sm tracking-[2px] uppercase font-bold border transition-colors hover:bg-white/10"
                     style={{ borderColor: T.cream, color: T.cream }}>
                    Programma Aziende <Users className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <div className="text-center">
                <div className="inline-block p-8 border-2 rounded-full" style={{ borderColor: T.gold }}>
                  <div className="text-7xl font-bold leading-none" style={{ color: T.goldSoft, ...serif }}>12</div>
                  <div className="text-xs uppercase tracking-widest text-white/70 mt-2">settimane</div>
                </div>
                <p className="text-white/50 text-xs mt-6 max-w-[220px] mx-auto">Percorso strutturato con checkpoint scientifici mensili</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FONTE + FOOTER */}
      <section className="py-12 px-6 md:px-14" style={{ backgroundColor: T.forest, color: T.cream }}>
        <div className="max-w-5xl mx-auto text-center">
          <Award className="w-8 h-8 mx-auto mb-4" style={{ color: T.goldSoft }} />
          <p className="text-xs uppercase tracking-[4px] mb-3" style={{ color: T.goldSoft }}>Rigore scientifico</p>
          <p className="text-white/50 text-xs md:text-sm leading-relaxed max-w-3xl mx-auto">
            Le informazioni riportate sono associazioni epidemiologiche osservate in popolazioni ampie e non implicano
            causalità diretta a livello individuale. Longevity Academy costruisce percorsi personalizzati basati sulle migliori
            evidenze disponibili, ma non sostituisce il consulto medico. Consultare sempre il proprio medico prima di iniziare
            un programma di allenamento, specialmente in presenza di patologie.
          </p>
        </div>
      </section>

      {/* NAV FOOTER */}
      <footer className="py-8 px-6 md:px-14 text-center" style={{ backgroundColor: "#000", color: T.cream }}>
        <div className="flex flex-wrap justify-center gap-6 text-xs uppercase tracking-widest">
          <a href="/longevity" className="hover:opacity-80" style={{ color: T.goldSoft }}>← Longevity Academy</a>
          <a href="/longevity/aziende" className="hover:opacity-80" style={{ color: T.goldSoft }}>Aziende & Affiliazioni</a>
          <a href="mailto:info@longevityacademy.it" className="hover:opacity-80" style={{ color: T.goldSoft }}>Contatti</a>
        </div>
      </footer>
    </div>
  );
};

export default LongevityVO2MaxPage;
